import { useQuery } from "@tanstack/react-query";

import { api } from "@/shared/lib/api";
import { QUERY_VOLATILITY } from "@/shared/config/api-constants";

export type FeatureFlags = {
  dashboard: boolean;
  rulebook: boolean;
  timeline: boolean;
  reviews_complaints: boolean;
  predictions: boolean;
  friends: boolean;
  /** Раздел «Инструменты» (`/tools/*`). KAN-925. */
  tools: boolean;
};

export type FeatureFlagName = keyof FeatureFlags;

const defaultFlags: FeatureFlags = {
  dashboard: false,
  rulebook: false,
  timeline: false,
  reviews_complaints: false,
  predictions: false,
  friends: false,
  tools: false,
};

async function fetchFeatureFlags(): Promise<FeatureFlags> {
  const { data } = await api.get<Record<string, boolean>>(
    "/settings/feature-flags",
  );
  return { ...defaultFlags, ...data };
}

/**
 * Состояние флагов вместе с признаком «ответ ещё не пришёл».
 *
 * Без `isLoading` вызывающий код не отличает «фича выключена» от «мы ещё
 * не знаем»: до ответа сервера все флаги равны `false`. Разница важна, и
 * направление по умолчанию у разных мест РАЗНОЕ:
 *
 * - меню/поиск: пока не знаем — НЕ показывать (иначе пункт мигнёт и пропадёт);
 * - маршрут фичи: пока не знаем — НЕ отдавать `notFound()`, подождать ответ
 *   (иначе включённая фича 404-ится на первом рендере).
 *
 * Ошибка запроса намеренно считается «узнали»: `isLoading` гаснет, флаги
 * остаются выключенными — фича прячется, а не подвешивает страницу навсегда.
 */
export type FeatureFlagsState = {
  flags: FeatureFlags;
  /** `true`, пока состояние флагов неизвестно (первый запрос не завершился). */
  isLoading: boolean;
};

export function useFeatureFlagsState(): FeatureFlagsState {
  const { data, isPending } = useQuery({
    queryKey: ["feature-flags"],
    queryFn: fetchFeatureFlags,
    ...QUERY_VOLATILITY.REFERENCE,
  });
  return { flags: data ?? defaultFlags, isLoading: isPending };
}

/**
 * Только значения флагов, без состояния загрузки: до ответа сервера всё
 * выключено. Годится там, где «показать позже» безобидно (блок на странице).
 * Для маршрутов и пунктов навигации нужен {@link useFeatureFlagsState}.
 */
export function useFeatureFlags(): FeatureFlags {
  return useFeatureFlagsState().flags;
}
